import axios from 'axios'

// hostname
const hostPath = window.location.origin

/**
 * Fetch a notification event
 * @param {Object} requestOpts Request options
 * @param {boolean} message Optional param to get message object back instead of standard data object
 */
export const fetcher = async (requestOpts, message = false) => {
  const { url, config } = requestOpts
  const options = { ...config }
  try {
    const response = await axios(url, options)
    return message ? Promise.resolve(response.data.message) : Promise.resolve(response.data.data)
  } catch (error) {
    if (error.response) {
      // The request was made and the server responded with a status code out of the 2xx range
      return handleAPIErrors(error.response)
    } else if (error.request) {
      // The request was made but no response was received
      return handleNetworkErrors()
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error', error.message)
    }
  }
}

/**
 * Handle network errors
 * They're not going to get fixed ... escape! escape!
 * @param {Object} error
 */
const handleNetworkErrors = () => {
  window.location = '/error'
}

/**
 * Handle API errors
 * @param {Object} response
 */
const handleAPIErrors = response => {
  // Unauthorized
  if (response.status === 401) {
    localStorage.setItem('_lastLocation', location.href)
    window.location = '/auth/authenticate'
  }
  // Catch all other API errors, let UI decide what to do
  return Promise.reject({
    statusText: response.statusText,
    status: response.status,
    type: response.responseType,
    url: response.url,
    ...response.data && { ...response.data },
  })
}

// -------------------------------------------------------------------------------------- req objects

/*----------------------------------------------------------- /v1/resources  */

/**
 * Fetch any resource
 * @param {Object { name: String, params: URLSearchParams }}}
 */
export const getResource = async ({ name, params }) => {
  const searchParams = new URLSearchParams({ ...params })
  const requestOpts = {
    url: `${hostPath}/v1/resource/${name}?${searchParams}`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Fetch a single network location
 * @param {String} type
 * @param {String | Number} id
 */
export const getResourceById = async (type, id) => {
  const requestOpts = {
    url: `${hostPath}/v1/resource/${type}/${id}`,
    config: {
      headers: {
        'Cache-Control': 'max-age=0',
      },
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Fetch resource schema
 * @param {String} type
 */
export const getResourceSchema = async type => {
  const requestOpts = {
    url: `${hostPath}/v1/resource/${type}/schema`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Fetch a single network location
 * @param {String} type
 * @param {String} id
 * @param {Object} updateObj
 * @param {String} params
 */
export const postResourceById = async (type, id = '', updateObj, params = '') => {
  const requestOpts = {
    url: `${hostPath}/v1/resource/${type}/${id}?type=${params}`,
    config: {
      method: 'POST',
      data: updateObj,
    },
  }
  // API response when deploying device returns message object
  // instead of standard data object so have to work around this
  return await fetcher(requestOpts, !!params)
}

/**
 * Manage a virtual machine on its host
 * @param {String | Number} id Virtual machine id
 * @param {String} request Action, one of 'create', 'delete' or 'restart'
 */
export const manageVirtualMachine = async (id, request) => {
  const requestOpts = {
    url: `${hostPath}/v1/resource/virtual-machine/${id}/manage?request=${encodeURIComponent(request)}`,
    config: {
      method: 'POST',
    },
  }
  // Unlike deploy-config, this endpoint nests its message inside the standard
  // data object ({data: {message}}), so unwrap it here instead of using the
  // fetcher's message mode
  const data = await fetcher(requestOpts)
  return data.message
}

/**
 * Delete a single network location
 * @param {String} type
 * @param {Number} id
 */
export const deleteResourceById = async (type, id) => {
  const requestOpts = {
    url: `${hostPath}/v1/resource/${type}/${id}`,
    config: {
      method: 'DELETE',
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Delete a single connection (AWSHC/Azure/Alibaba)
 * @param {Number} connectionId - connection id to be deleted
 * @param {Number} connectionType - connection type to be deleted
 */
export const deleteConnectionById = async (connectionId, connectionType) => {
  const requestOpts = {
    url: `${hostPath}/v2/service/${connectionId}/${connectionType}`,
    config: {
      method: 'DELETE',
    },
  }
  // API response when deleting an AWSHC/Azure/Alibaba connection returns a message string
  // instead of standard data object so we pass in true so the fetcher looks in the right place
  return await fetcher(requestOpts, true)
}

/**
 * Fetch a single network location
 * @param {String} type
 * @param {Object} updateObj
 */
export const createResource = async (type, updateObj) => {
  const requestOpts = {
    url: `${hostPath}/v1/resource/${type}`,
    config: {
      method: 'POST',
      data: updateObj,
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Attach a cross connect to a service
 * @param {String} method POST or DELETE
 * @param {String} serviceId
 * @param {String} crossConnectId
 */
export const crossConnectService = async (method, serviceId, crossConnectId) => {
  const requestOpts = {
    url: `${hostPath}/v2/service/${serviceId}/crossconnect/${crossConnectId}`,
    config: {
      method: method,
    },
  }
  return await fetcher(requestOpts)
}

/*----------------------------------------------------------- /v1/mega  */

/**
 * Fetch public locations for the network map (GET /v2/location)
 */
export const getV2Locations = async () => {
  const requestOpts = {
    url: `${hostPath}/v2/location`,
    config: {
      headers: {
        'Cache-Control': 'max-age=0',
      },
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Fetch active carrier circuits for the network map (GET /v2/map/active-carrier-circuits)
 */
export const getMapActiveCarrierCircuits = async () => {
  const requestOpts = {
    url: `${hostPath}/v2/map/active-carrier-circuits`,
    config: {
      headers: {
        'Cache-Control': 'max-age=0',
      },
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Fetch a list of megaport enabled locations using the V1 api
 */
export const getLocations = async () => {
  const requestOpts = {
    url: `${hostPath}/v1/mega/locations`,
    config: {
      headers: {
        'Cache-Control': 'max-age=0',
      },
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}
/**
 * Fetch the prouduct with diversity info
 */
export const getProducts = async () => {
  const requestOpts = {
    url: `${hostPath}/v1/mega/location/products`,
    config: {
      headers: {
        'Cache-Control': 'max-age=0',
      },
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}
/**
 * Fetch products to single location
 * @param {String} locationid
 */
export const getProductsByLocation = async locationid => {
  const requestOpts = {
    url: `${hostPath}/v1/mega/location/${locationid}/products`,
    config: {
      headers: {
        'Cache-Control': 'max-age=0',
      },
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

export const getProductsByLocationId = async locationid => {
  const requestOpts = {
    url: `${hostPath}/v1/resource/location/product?location_id=${locationid}`,
    config: {
      headers: {
        'Cache-Control': 'max-age=0',
      },
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}
/**
 * Fetch the ports with diversity info
 */
export const getPorts = async () => {
  const requestOpts = {
    url: `${hostPath}/v1/mega/location/ports`,
    config: {
      headers: {
        'Cache-Control': 'max-age=0',
      },
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}
/**
 * Fetch ports to single location
 * @param {String} locationid
 */
export const getPortsByLocation = async locationid => {
  const requestOpts = {
    url: `${hostPath}/v1/mega/location/${locationid}/ports`,
    config: {
      headers: {
        'Cache-Control': 'max-age=0',
      },
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Enable Port Diversity
 * @param {String | Number} id
 * @param {String} portSpeed
 */
export const enablePortDiversity = async (id, portSpeed) => {
  const requestOpts = {
    url: `${hostPath}/v1/resource/location/${id}/enable-diversity?port_speed=${portSpeed}`,
    config: {
      method: 'POST',
    },
  }
  return await fetcher(requestOpts)
}

export const addLocationProduct = async data => {
  const requestOpts = {
    url: `${hostPath}/v1/resource/location/product`,
    config: {
      method: 'POST',
      data,
    },
  }
  return await fetcher(requestOpts)
}

export const removeLocationProduct = async id => {
  const requestOpts = {
    url: `${hostPath}/v1/resource/location/product/${id}`,
    config: {
      method: 'DELETE',
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Fetch a list of megaport enabled locations by country
 * @param {String} locationid
 */
export const getLocationsByCountry = async locationid => {
  const requestOpts = {
    url: `${hostPath}/v1/mega/locations-by/${locationid}`,
    config: {
      headers: {
        'Cache-Control': 'max-age=0',
      },
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Fetch a given megaport services
 * @param {String} serviceId
 * @param {Boolean} withParams
 */
export const getService = async (serviceId, withParams = false) => {
  const params = withParams ? {
    'inc-services': 1,
    'inc-portal': 1,
    'inc-resources': 1,
    'inc-dates': 1,
    'inc-csp': 1,
    'inc-related': 1,
  } : {}

  const requestOpts = {
    url: `${hostPath}/v2/service/${serviceId}`,
    config: {
      headers: {
        'Cache-Control': 'max-age=0',
      },
      method: 'GET',
      params,
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Update Megaport IP Transit services
 * @param {String} serviceId
 * @param {Object} payload
 */
export const updateIpTransit = async (serviceId, payload) => {
  const requestOpts = {
    url: `${hostPath}/v2/service/${serviceId}/ip-transit`,
    config: {
      method: 'POST',
      data: payload,
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Update BGP communities on an IP Transit service
 * @param {String} serviceId
 * @param {Array<Object>} payload
 */
export const updateBgpCommunity = async (serviceId, payload) => {
  const requestOpts = {
    url: `${hostPath}/v2/service/${serviceId}/bgp-community`,
    config: {
      method: 'POST',
      data: payload,
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Update BGP communities on a BGP connection resource linked to an IP Transit service
 * @param {Number} bgpConnectionId
 * @param {Array<Object>} payload
 */
export const updateBgpConnectionCommunity = async (bgpConnectionId, payload) => {
  const requestOpts = {
    url: `${hostPath}/v2/ip-transit/bgp-connection/${bgpConnectionId}/community`,
    config: {
      method: 'POST',
      data: payload,
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Fetch megaport services
 * @param {Array<String>} serviceIds
 * @param {Object} configParams
 */
export const getServices = async (serviceIds, configParams = { 'inc-portal': 1 }) => {
  const requestOpts = {
    url: `${hostPath}/v2/services`,
    config: {
      method: 'POST',
      data: serviceIds,
      params: configParams,
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Fetch megaport services with custom config params
 */
export const getServicesWithParams = async (serviceIds, customParams = []) => {
  const params = customParams.length > 0 ? {
    'inc-services': customParams.includes('services') ? 1 : 0,
    'inc-portal': customParams.includes('portal') ? 1 : 0,
    'inc-resources': customParams.includes('resources') ? 1 : 0,
    'inc-dates': customParams.includes('dates') ? 1 : 0,
    'inc-csp': customParams.includes('csp') ? 1 : 0,
    'inc-related': customParams.includes('related') ? 1 : 0,
  } : {}

  const requestOpts = {
    url: `${hostPath}/v2/services`,
    config: {
      method: 'POST',
      data: serviceIds,
      params,
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Fetch the port summary
 */
export const getPortSummary = async () => {
  const requestOpts = {
    url: `${hostPath}/v1/mega/port-summary`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Get service logs for a given service
 * @param {String} serviceId
 */
export const getServiceLogs = async serviceId => {
  const requestOpts = {
    url: `${hostPath}/v2/service/${serviceId}/log`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Get deploy-agent logs for a given service
 * @param {String} serviceId
 */
export const getDeployAgentLogs = async serviceId => {
  const requestOpts = {
    url: `${hostPath}/v1/resource/deploy-log`,
    config: {
      method: 'GET',
      params: {
        service_id: `${serviceId}`,
      },
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Get every deployment manifest of a service, oldest first, each with its deployment jobs (empty list if none)
 * @param {String} serviceId
 */
export const getDeploymentManifests = async serviceId => {
  const requestOpts = {
    url: `${hostPath}/v2/service/${serviceId}/deployment-manifests`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Queue a replacement for one of a service's current deployment jobs
 * Resolves to { message, deployment_job_id, replaced_id, service_status }
 * @param {String | Number} serviceId
 * @param {String | Number} jobId Deployment job being replaced
 */
export const replaceDeploymentJob = async (serviceId, jobId) => {
  const requestOpts = {
    url: `${hostPath}/v2/service/${serviceId}/deployment-job/${jobId}/replace`,
    config: {
      method: 'POST',
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Get service logs for a given service
 * @param {String} serviceId
 */
export const getServiceDiagnosticsId = async serviceId => {
  const requestOpts = {
    url: `${hostPath}/v2/service/${serviceId}/diagnostics`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

/**
* Get service logs for a given service
* @param {String} serviceId
* @param {String} operationId
 */
export const getServiceDiagnostics = async (operationId, serviceId) => {
  const requestOpts = {
    url: `${hostPath}/v2/service/${serviceId}/diagnostics/${operationId}`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Fetch company details
 * @param {String} companyId
 */
export const getCompanyDetails = async companyId => {
  const requestOpts = {
    url: `${hostPath}/v1/mega/company/${companyId}`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Fetch services by company
 * @param {String} companyId
 */
export const getCompanyServices = async companyId => {
  const requestOpts = {
    url: `${hostPath}/v1/mega/services-by/company/${companyId}`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Fetch history by recource type and id
 * @param {String} resourceId
 * @param {String} recourcetype
 */
export const getHistoryByResourceDetail = async (recourcetype, resourceId) => {
  const requestOpts = {
    url: `${hostPath}/v1/${recourcetype}/${resourceId}/history`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

/*----------------------------------------------------------- /v1/misc  */

/**
 * Search a given resource for a given query param
 * @param {String} resourceName The resource to search (company, asn, location)
 * @param {String} queryParam The string text query
 */
export const searchResources = async (resourceName, queryParam) => {
  const requestOpts = {
    url: `${hostPath}/v2/search/${resourceName}/${queryParam}`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}
/**
 * Fetch the interface list options by device
 * @param {String} deviceId
 */
export const getInterfacesByDevice = async (deviceId, isAvailable) => {
  const requestOpts = {
    url: `${hostPath}/v1/resource/interface?available=${isAvailable}&device_id=${deviceId}`,
    config: {
      cache: 'default',
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}
/**
 * Fetch the interfaces by circuit
 * @param {String} circuitId
 */
export const getCircuitInterfaces = async circuitId => {
  const requestOpts = {
    url: `${hostPath}/v1/resource/circuit/${circuitId}/interface`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}
/**
 * Update the interfaces to a circuit
 *
 * @param {String} circuitId
 * @param {String} queryParam to add ,remove,replace
 * @param {Object} interfaceIds
 */
export const interfaceCircuit = async (circuitId, queryParam, interfaceIds) => {
  const requestOpts = {
    url: `${hostPath}/v1/resource/circuit/${circuitId}/interface?action=${queryParam}`,
    config: {
      method: 'POST',
      data: interfaceIds,
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Find a service or company by UUID
 * @param {String} queryParam a company or service UUID
 */
export const findCompanyService = async queryParam => {
  const requestOpts = {
    url: `${hostPath}/v1/find/${queryParam}`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Fetch the provisioning details of a service by service Id
 * @param {String} queryParam a service Id
 */
export const getProvisioningByServiceId = async serviceId => {
  const provisioning = {
    url: `${hostPath}/v2/service/${serviceId}/check-provisioning`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(provisioning)
}

/**
 * Get the current user details
 */
export const getUserSelf = async () => {
  const requestOpts = {
    url: `${hostPath}/v1/user`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Get the current user details
 * @param {String} uid
 */
export const getUser = async uid => {
  const requestOpts = {
    url: `${hostPath}/v1/user/${uid}`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Get the megaport user list
 */
export const getUserList = async () => {
  const requestOpts = {
    url: `${hostPath}/v1/users`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Update user group
 * @param {String} uid
 * @param {Object} groups
 */
export const updateUserGroups = async (uid, groups) => {
  const requestOpts = {
    url: `${hostPath}/v1/user/${uid}/groups`,
    config: {
      method: 'POST',
      data: groups,
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Generate a user key
 * @param {String} uid
 * @param {String} keyId
 */
export const generateApiKey = async (uid, key) => {
  const requestOpts = {
    url: `${hostPath}/v1/user/${uid}/apikey`,
    config: {
      method: 'POST',
      data: key,
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Generate a user key
 * @param {String} uid
 * @param {String} keyId
 */
export const deleteApiKey = async (uid, keyId) => {
  const requestOpts = {
    url: `${hostPath}/v1/user/${uid}/apikey/${keyId}`,
    config: {
      method: 'DELETE',
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Change user's TACACS password
 * @param {String} uid
 * @param {String} passwordHash
 */
export const changeTacacsPassword = async (uid, passwordHash) => {
  const requestOpts = {
    url: `${hostPath}/v1/user/${uid}`,
    config: {
      method: 'POST',
      data: {
        password_hash: passwordHash,
      },
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Update user's authorized SSH public keys
 * @param {String} uid
 * @param {String} authorizedKeys
 */
export const updateSshKeys = async (uid, authorizedKeys) => {
  const requestOpts = {
    url: `${hostPath}/v1/user/${uid}`,
    config: {
      method: 'POST',
      data: {
        authorized_keys: authorizedKeys,
      },
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Get a company employee user details
 * @param {String} personUid
 */
export const getCompanyUser = async personUid => {
  const requestOpts = {
    url: `${hostPath}/v1/person/${personUid}`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Get a prefix list details by service id & prefix list id
 * @param {Number | String} serviceId
 * @param {Number | String} prefixlistId
 */
export const getPrefixlistDetails = async (serviceId, prefixlistId) => {
  const requestOpts = {
    url: `${hostPath}/v2/service/${serviceId}/prefixlist/${prefixlistId}`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Get a paacket filter list details by service id & packet filter id
 * @param {Number | String} serviceId
 * @param {Number | String} packtfilterId
 */
export const getPacketFilter = async (serviceId, packtfilterId) => {
  const requestOpts = {
    url: `${hostPath}/v2/service/${serviceId}/packetfilter/${packtfilterId}`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Search Forge
 * @param {String} query Search text
 */
export const searchRequest = async query => {
  const encodedQuery = encodeURIComponent(query)
  const requestOpts = {
    url: `${hostPath}/v1/search?q=${encodedQuery}`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}
/**
 * Logout Forge
 */
export const logout = async () => {
  const requestOpts = {
    url: `${hostPath}/v1/logout`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

//**************** Organisation contacts ***********/
/**
 * Get list of organisation contacts
 * @param { Number } id organisation id
 */
export const getOrganisationContacts = async id => {
  const requestOpts = {
    url: `${hostPath}/v1/resource/organisation-contact?organisation_id=${id}`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Add organisation contact
 * @param { Object } payload request payload
 */
export const addOrganisationContact = async payload => {
  const requestOpts = {
    url: `${hostPath}/v1/resource/organisation-contact`,
    config: {
      method: 'POST',
      data: payload,
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Update organisation contact details
 * @param { Number } contactId to be updated
 * @param { Object } payload request data
 */
export const updateOrganisationContact = async (contactId, payload) => {
  const requestOpts = {
    url: `${hostPath}/v1/resource/organisation-contact/${contactId}`,
    config: {
      method: 'POST',
      data: payload,
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Delete organisation contact
 * @param { Number } contactId contact Id to be deleted
 */
export const deleteOrganisationContact = async contactId => {
  const requestOpts = {
    url: `${hostPath}/v1/resource/organisation-contact/${contactId}`,
    config: {
      method: 'DELETE',
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Set Traffic Class
 * @param { String } serviceId to be updated
 * @param { Number } trafficClass to set (0, 2 or 4)
 */
export const setTrafficClass = async (serviceId, trafficClass) => {
  const requestOpts = {
    url: `${hostPath}/v2/service/${serviceId}/traffic-class`,
    config: {
      method: 'POST',
      data: { traffic_class: trafficClass },
    },
  }
  return await fetcher(requestOpts)
}

/**
 * Set VLL Congestion Mitigation flag
 * @param { String } serviceId to be updated
 */
export const setVllCongestionMitigation = async (serviceId, value) => {
  const requestOpts = {
    url: `${hostPath}/v2/service/${serviceId}/congestion-mitigation`,
    config: {
      method: 'POST',
      data: {
        value: !!value,
      },
    },
  }
  return await fetcher(requestOpts)
}

export const getCurrentServerTime = async () => {
  const requestOpts = {
    url: `${hostPath}/v1/time`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

export const getPatchingDevices = async () => {
  const requestOpts = {
    url: `${hostPath}/v1/patching/devices`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

export const getPatchingDevice = async id => {
  const requestOpts = {
    url: `${hostPath}/v1/patching/device/${id}`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

export const getPatchingPatchPanels = async () => {
  const requestOpts = {
    url: `${hostPath}/v1/patching/patch-panels`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

export const getPatchingPatchPanel = async id => {
  const requestOpts = {
    url: `${hostPath}/v1/patching/patch-panel/${id}`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

export const updatePatchPort = async (id, data) => {
  const requestOpts = {
    url: `${hostPath}/v1/resource/patch-port/${id}`,
    config: {
      method: 'POST',
      data,
    },
  }
  return await fetcher(requestOpts)
}

export const createPatchPort = async data => {
  const requestOpts = {
    url: `${hostPath}/v1/resource/patch-port`,
    config: {
      method: 'POST',
      data,
    },
  }
  return await fetcher(requestOpts)
}


export const getInterfaceHistory = async id => {
  const requestOpts = {
    url: `${hostPath}/v1/resource/interface/${id}/extended-history`,
    config: {
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

export const getVirtualMachineImagePlatforms = async id => {
  const requestOpts = {
    url: `${hostPath}/v1/resource/virtual-machine-image/${id}/platforms`,
    config: {
      headers: {
        'Cache-Control': 'max-age=0',
      },
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

export const updateVirtualMachineImagePlatforms = async (id, platformIds) => {
  const requestOpts = {
    url: `${hostPath}/v1/resource/virtual-machine-image/${id}/platforms`,
    config: {
      method: 'POST',
      data: {
        virtualisation_platform_ids: platformIds,
      },
    },
  }
  return await fetcher(requestOpts)
}

export const getIPTransitNetworkDDOSServers = async ipTransitNetworkId => {
  const requestOpts = {
    url: `${hostPath}/v2/ip-transit/ddos-servers/${ipTransitNetworkId}`,
    config: {
      headers: {
        'Cache-Control': 'max-age=0',
      },
      method: 'GET',
    },
  }
  return await fetcher(requestOpts)
}

export const updateIPTransitNetworkDDOSServers = async (ipTransitNetworkId, deviceIds) => {
  const requestOpts = {
    url: `${hostPath}/v2/ip-transit/ddos-servers`,
    config: {
      method: 'POST',
      data: {
        ip_transit_network_id: ipTransitNetworkId,
        device_ids: deviceIds,
      },
    },
  }
  return await fetcher(requestOpts)
}

export const assignIPTransitNetworkIPNetwork = async (ipTransitNetworkId, ipVersion, masklen) => {
  const requestOpts = {
    url: `${hostPath}/v1/resource/ip-transit-network/${ipTransitNetworkId}/ip-network`,
    config: {
      method: 'POST',
      data: {
        ip_version: ipVersion,
        masklen,
      },
    },
  }
  return await fetcher(requestOpts)
}
